import { useQuery } from "@tanstack/react-query";
import { useFieldArray, useForm, useWatch } from "react-hook-form";

import type { BuildingDto, RemsProjectConfigurationDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadio, FormRadioGroup } from "@/components/Form/FormRadio";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Accordion } from "@/design-system/Accordion";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useBuildingQueries } from "@/queries/buildings/queryOptions";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { RemsReconciliationSection } from "./RemsReconciliationSection";

export type RemsFormValues = RemsProjectConfigurationDto;

type RemsFormValuesInternal = RemsFormValues & {
  complexCodeMode: "single" | "perBuilding";
};

export function RemsPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();
  const buildingQueries = useBuildingQueries();

  const {
    data: config,
    isFetching: isLoadingConfig,
    error: configError,
  } = useQuery(integrationQueries.remsConfiguration());
  const { data: buildings, isFetching: isLoadingBuildings, error: buildingsError } = useQuery(buildingQueries.list());
  const mutation = integrationMutations.useEditRemsConfiguration();

  if (!hasPermission((x) => x.integrations.canManageRems)) {
    return <ErrorPage status={404} />;
  }

  if (configError || buildingsError) {
    return <ErrorPage error={configError || buildingsError} />;
  }

  if (isLoadingConfig || isLoadingBuildings) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.rems.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <Stack gap="xl">
        <RemsForm
          configureWhileDisabled
          defaultValues={{
            tickets: {
              enabled: config?.tickets?.enabled || false,
              syncClosedStatus: config?.tickets?.syncClosedStatus || false,
              sendTicketUpdates: config?.tickets?.sendTicketUpdates || false,
              autoSendTickets: config?.tickets?.autoSendTickets || false,
              defectCode: config?.tickets?.defectCode || "",
            },
            contracts: {
              enabled: config?.contracts?.enabled || false,
              syncContacts: config?.contracts?.syncContacts || false,
            },
            communityUserContactId: config?.communityUserContactId || "",
            communityUserRelationId: config?.communityUserRelationId || "",
            complexCode: config?.complexCode || "",
            tenantId: config?.tenantId || "",
            buildingComplexCodes: config?.buildingComplexCodes || [],
          }}
          buildings={buildings?.items ?? []}
          isSubmitting={mutation.isPending}
          onSubmit={mutation.mutate}
        />
        <RemsReconciliationSection isActive={!!config?.contracts?.enabled && !!config.contracts.syncContacts} />
      </Stack>
    </DocumentPage>
  );
}

/**
 * Renders the reusable REMS configuration form. `configureWhileDisabled` permits editing inactive sections, and
 * `onSubmit` receives the normalized `RemsFormValues` payload.
 */
export function RemsForm({
  buildings,
  configureWhileDisabled = false,
  defaultValues,
  isSubmitting,
  onSubmit,
  submitLabel,
}: {
  buildings: BuildingDto[];
  configureWhileDisabled?: boolean;
  defaultValues: RemsFormValues;
  isSubmitting: boolean;
  onSubmit: (data: RemsFormValues) => void;
  submitLabel?: string;
}): React.ReactNode {
  const { t } = useTranslation();

  const hasPerBuildingCodes = defaultValues.buildingComplexCodes?.some((x) => x.complexCode);
  const internalDefaultValues: RemsFormValuesInternal = {
    ...defaultValues,
    complexCodeMode: hasPerBuildingCodes ? "perBuilding" : "single",
    buildingComplexCodes: buildings.map((b) => ({
      buildingId: b.id,
      complexCode: defaultValues.buildingComplexCodes?.find((x) => x.buildingId === b.id)?.complexCode ?? "",
    })),
  };

  const formMethods = useForm<RemsFormValuesInternal>({ defaultValues: internalDefaultValues });
  const ticketsEnabled = useWatch({ control: formMethods.control, name: "tickets.enabled" });
  const contractsEnabled = useWatch({ control: formMethods.control, name: "contracts.enabled" });
  const syncContactsEnabled = useWatch({ control: formMethods.control, name: "contracts.syncContacts" });
  const complexCodeMode = useWatch({ control: formMethods.control, name: "complexCodeMode" });
  const anyEnabled = ticketsEnabled || contractsEnabled;
  const configurationEditable = anyEnabled || configureWhileDisabled;

  const { fields: buildingComplexCodeFields } = useFieldArray({
    control: formMethods.control,
    name: "buildingComplexCodes",
  });

  const handleSubmit = ({ complexCodeMode: mode, ...formValues }: RemsFormValuesInternal) => {
    if (mode === "single") {
      onSubmit({ ...formValues, buildingComplexCodes: [] });
    } else {
      onSubmit({ ...formValues, complexCode: "" });
    }
  };

  return (
    <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
      <Form.Container title={t("page.integration.rems.title")}>
        <Form.Body>
          <FormField label={t("page.integration.rems.form.tickets.label")}>
            <FormRadioGroup layout="vertical">
              <FormCheckbox<RemsFormValuesInternal, "tickets.enabled">
                name="tickets.enabled"
                label={t("common.action.enable")}
                onChange={(e) => {
                  if (!e.target.checked) {
                    formMethods.setValue("tickets.syncClosedStatus", false);
                    formMethods.setValue("tickets.sendTicketUpdates", false);
                    formMethods.setValue("tickets.autoSendTickets", false);
                  }
                }}
              />
              <FormCheckbox<RemsFormValuesInternal, "tickets.syncClosedStatus">
                name="tickets.syncClosedStatus"
                label={t("page.integration.rems.form.tickets.sync-closed-status")}
                disabled={!ticketsEnabled}
              />
              <FormCheckbox<RemsFormValuesInternal, "tickets.sendTicketUpdates">
                name="tickets.sendTicketUpdates"
                label={t("page.integration.rems.form.tickets.send-ticket-updates")}
                disabled={!ticketsEnabled}
              />
              <FormCheckbox<RemsFormValuesInternal, "tickets.autoSendTickets">
                name="tickets.autoSendTickets"
                label={t("page.integration.rems.form.tickets.auto-send-tickets")}
                disabled={!ticketsEnabled}
              />
            </FormRadioGroup>
          </FormField>

          <FormField
            label={t("page.integration.rems.form.defect-code")}
            required
            tooltip={t("page.integration.rems.form.defect-code.tooltip")}
          >
            <FormInput<RemsFormValuesInternal, "tickets.defectCode">
              name="tickets.defectCode"
              rules={{
                validate: {
                  required: (defectCode, formValues) => {
                    if (formValues.tickets.enabled) {
                      return createRequiredStringRule(t, "page.integration.rems.form.defect-code")(
                        defectCode,
                        formValues,
                      );
                    }
                  },
                },
              }}
              placeholder="AA00000"
              disabled={!ticketsEnabled && !configureWhileDisabled}
            />
          </FormField>

          <FormField label={t("page.integration.rems.form.users.label")}>
            <FormRadioGroup layout="vertical">
              <FormCheckbox<RemsFormValuesInternal, "contracts.enabled">
                name="contracts.enabled"
                label={t("common.action.enable")}
                onChange={(e) => {
                  if (!e.target.checked) {
                    formMethods.setValue("contracts.syncContacts", false);
                  }
                }}
              />
              <FormCheckbox<RemsFormValuesInternal, "contracts.syncContacts">
                name="contracts.syncContacts"
                label={t("page.integration.rems.form.users.sync-contacts")}
                disabled={!contractsEnabled}
              />
            </FormRadioGroup>
          </FormField>

          <p className="text-body-emphasis">{t("page.integration.rems.form.common.label")}</p>

          <FormField label={t("page.integration.rems.form.tenant-id")} required>
            <FormInput<RemsFormValuesInternal, "tenantId">
              name="tenantId"
              rules={{
                validate: {
                  required: (tenantId, formValues) => {
                    if (formValues.tickets.enabled || formValues.contracts.enabled) {
                      return createRequiredStringRule(t, "page.integration.rems.form.tenant-id")(tenantId, formValues);
                    }
                  },
                },
              }}
              placeholder="00000000-0000-0000-0000-000000000000"
              disabled={!configurationEditable}
            />
          </FormField>

          <FormField
            label={t("page.integration.rems.form.community-user-contact-id")}
            required
            tooltip={t("page.integration.rems.form.community-user-contact-id.tooltip")}
          >
            <FormInput<RemsFormValuesInternal, "communityUserContactId">
              name="communityUserContactId"
              rules={{
                validate: {
                  required: (communityUserContactId, formValues) => {
                    if (formValues.tickets.enabled || formValues.contracts.enabled) {
                      return createRequiredStringRule(t, "page.integration.rems.form.community-user-contact-id")(
                        communityUserContactId,
                        formValues,
                      );
                    }
                  },
                },
              }}
              placeholder="1234"
              disabled={!configurationEditable}
            />
          </FormField>

          <FormField
            label={t("page.integration.rems.form.community-user-relation-id")}
            required
            tooltip={t("page.integration.rems.form.community-user-relation-id.tooltip")}
          >
            <FormInput<RemsFormValuesInternal, "communityUserRelationId">
              name="communityUserRelationId"
              rules={{
                validate: {
                  required: (communityUserRelationId, formValues) => {
                    if (formValues.tickets.enabled || formValues.contracts.enabled) {
                      return createRequiredStringRule(t, "page.integration.rems.form.community-user-relation-id")(
                        communityUserRelationId,
                        formValues,
                      );
                    }
                  },
                },
              }}
              placeholder="1234"
              disabled={!configurationEditable}
            />
          </FormField>

          <FormField
            label={t("page.integration.rems.form.complex-code")}
            required={syncContactsEnabled && complexCodeMode === "single"}
            tooltip={t("page.integration.rems.form.complex-code.tooltip")}
          >
            <FormRadioGroup layout="horizontal">
              <FormRadio<RemsFormValuesInternal, "complexCodeMode">
                name="complexCodeMode"
                value="single"
                label={t("page.integration.rems.form.complex-code.single")}
                disabled={!configurationEditable}
              />
              <FormRadio<RemsFormValuesInternal, "complexCodeMode">
                name="complexCodeMode"
                value="perBuilding"
                label={t("page.integration.rems.form.complex-code.per-building")}
                disabled={!configurationEditable}
              />
            </FormRadioGroup>

            {complexCodeMode === "single" ? (
              <FormInput<RemsFormValuesInternal, "complexCode">
                name="complexCode"
                rules={{
                  validate: {
                    required: (complexCode, formValues) => {
                      if (formValues.contracts.syncContacts) {
                        return createRequiredStringRule(t, "page.integration.rems.form.complex-code")(
                          complexCode,
                          formValues,
                        );
                      }
                    },
                  },
                }}
                placeholder="0000.0000"
                disabled={!configurationEditable}
              />
            ) : (
              <Accordion.Root>
                <Accordion.Item value="building-complex-codes">
                  <Accordion.Trigger>{t("page.integration.rems.form.complex-code")}</Accordion.Trigger>
                  <Accordion.Content>
                    <Stack gap="sm">
                      {buildingComplexCodeFields.map((field, index) => (
                        <Inline key={field.id} gap="sm" align="center">
                          <span className="w-select-responsive-filter shrink-0 text-caption">
                            {buildings[index]?.name}
                          </span>
                          <FormInput<RemsFormValuesInternal>
                            name={`buildingComplexCodes.${index}.complexCode`}
                            placeholder="0000.0000"
                            disabled={!configurationEditable}
                            rules={{
                              validate: {
                                required: (complexCode, formValues) => {
                                  if (formValues.contracts.syncContacts) {
                                    return createRequiredStringRule(t, "page.integration.rems.form.complex-code")(
                                      complexCode,
                                      formValues,
                                    );
                                  }
                                },
                              },
                            }}
                          />
                        </Inline>
                      ))}
                    </Stack>
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion.Root>
            )}
          </FormField>
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {submitLabel ?? t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
